import { Box, useTheme } from '@mui/material';
import { ILockerGateItem } from 'api/interfaces/lockerGate';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, getOrderPayment, parseAndFormatPrice } from 'shared';
import { InfIcon } from 'assets/icons/InfIcon';
import { StyledBodyRight, StyledBodyType, StyledCardBody } from '../../StyledComponents';

interface RowItemBodyProps {
	item: ILockerGateItem;
}

export const RowItemBody = ({ item }: RowItemBodyProps) => {
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const { isLocker, lockerDetails } = item;

	const area = lockerDetails?.lockerDetailsInfo?.area;
	const pricing = lockerDetails?.pricing;
	const rentalDetails = lockerDetails?.rentalDetails;

	const formatPrice = (value?: number) => parseAndFormatPrice(value, 2, false, i18n.language);
	const priceText =
		pricing && !rentalDetails
			? pricing.privatePrice === pricing.companyPrice
				? `${formatPrice(pricing.privatePrice)} ${pricing.currency}`
				: `${formatPrice(pricing.privatePrice)} / ${formatPrice(pricing.companyPrice)} ${pricing.currency}`
			: undefined;

	const tag = lockerDetails?.orderItemTypeTag;
	const genderContext = tag === '#offices' ? 'offices' : tag === '#services' ? 'services' : undefined;
	const genderOptions = genderContext ? { context: genderContext } : undefined;

	const isPaid = rentalDetails?.isPaid;
	const paymentText = (() => {
		if (!isLocker || !rentalDetails || isPaid == null) {
			return undefined;
		}
		if (isPaid) {
			const word = t('common.paid', genderOptions);
			return rentalDetails.paidTo ? `${word} ${t('common.to')} ${formatDate(rentalDetails.paidTo)}` : word;
		}
		// Unpaid: same text as the "Więcej" details view.
		return getOrderPayment(rentalDetails, t);
	})();

	// Rental end pulled onto the tile; open-ended rentals show the infinity mark instead of a date.
	const rentalEndNode = (() => {
		if (!isLocker || !rentalDetails) {
			return undefined;
		}
		if (rentalDetails.rentedTo) {
			return `${t('common.to')} ${formatDate(rentalDetails.rentedTo)}`;
		}
		return (
			<Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
				{t('common.to')} <InfIcon width={22} height={10} />
			</Box>
		);
	})();

	const areaText = isLocker && area != null ? `${parseAndFormatPrice(area, 2, true, i18n.language)} m²` : undefined;
	// Area is shown on every box type; rented boxes append it before the rental-end info: "4,5 m² · do ...".
	const leftParts = [areaText, rentalEndNode].filter(Boolean);
	const leftTitle =
		[
			areaText,
			isLocker && rentalDetails
				? rentalDetails.rentedTo
					? `${t('common.to')} ${formatDate(rentalDetails.rentedTo)}`
					: `${t('common.to')} ∞`
				: undefined,
		]
			.filter(Boolean)
			.join(' · ') || undefined;
	const rightText = priceText ?? paymentText;

	if (leftParts.length === 0 && !rightText) {
		return null;
	}

	return (
		<StyledCardBody>
			<StyledBodyType title={leftTitle}>
				{leftParts.map((part, index) => (
					<Fragment key={index}>
						{index > 0 && ' · '}
						{part}
					</Fragment>
				))}
			</StyledBodyType>
			<StyledBodyRight
				title={rightText}
				sx={
					paymentText
						? { color: isPaid ? theme.palette.green.main : theme.palette.error.light, fontWeight: 600 }
						: undefined
				}
			>
				{rightText ?? ''}
			</StyledBodyRight>
		</StyledCardBody>
	);
};
